/**
 * Accessible tooltip with portal: shows content on hover and focus.
 * Renders tooltip bubble via React Portal to document.body to escape overflow clipping.
 * Uses position: fixed with computed coordinates from getBoundingClientRect().
 * No native title attribute to prevent duplicate tooltips.
 */
import { useState, useId, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

const TOOLTIP_GAP = 8; // Gap between trigger and tooltip
const TOOLTIP_MIN_HEIGHT = 40; // Approximate min tooltip height
const VIEWPORT_MARGIN = 8; // Margin from viewport edges

function computeTooltipPosition(triggerRef) {
  if (!triggerRef.current) return null;
  
  const rect = triggerRef.current.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  
  // Check space above and below
  const spaceAbove = rect.top;
  const spaceBelow = viewportHeight - rect.bottom;
  const positionAbove = spaceAbove > TOOLTIP_MIN_HEIGHT + TOOLTIP_GAP;
  
  // Horizontal: center on trigger, clamp to viewport (account for tooltip width)
  const tooltipWidth = 280; // maxWidth
  const tooltipHalfWidth = tooltipWidth / 2;
  let x = rect.left + rect.width / 2;
  // Clamp so tooltip doesn't extend beyond viewport edges
  const minX = VIEWPORT_MARGIN + tooltipHalfWidth;
  const maxX = viewportWidth - VIEWPORT_MARGIN - tooltipHalfWidth;
  x = Math.max(minX, Math.min(maxX, x));
  
  // Vertical: above or below based on available space
  // y is the top edge of the tooltip
  const y = positionAbove
    ? rect.top - TOOLTIP_GAP // Will be translated up by 100% (tooltip height)
    : rect.bottom + TOOLTIP_GAP; // Will be at this y (no translateY)
  
  return { x, y, positionAbove };
}

export default function Tooltip({ children, content, asButton = true, href, target, rel, ariaLabel, ...rest }) {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState(null);
  const triggerRef = useRef(null);
  const id = useId().replace(/:/g, '-');
  const tooltipId = `tooltip-${id}`;

  if (!content || String(content).trim() === '') {
    return children;
  }

  // Update position when visible or on scroll/resize
  useEffect(() => {
    if (!visible) return;
    
    const updatePosition = () => {
      const pos = computeTooltipPosition(triggerRef);
      if (pos) setPosition(pos);
    };
    
    updatePosition();
    window.addEventListener('scroll', updatePosition, true); // Capture phase
    window.addEventListener('resize', updatePosition);
    
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [visible]);

  const handleShow = () => {
    const pos = computeTooltipPosition(triggerRef);
    setPosition(pos);
    setVisible(true);
  };

  const handleHide = () => {
    setVisible(false);
    setPosition(null);
  };

  const baseStyle = { display: 'inline-flex', ...rest.style };
  if (asButton && !href) {
    baseStyle.border = 'none';
    baseStyle.background = 'none';
    baseStyle.padding = 0;
    baseStyle.cursor = 'help';
    baseStyle.fontSize = 'inherit';
  }
  if (href) {
    baseStyle.cursor = 'pointer';
    baseStyle.textDecoration = 'none';
    baseStyle.color = 'inherit';
  }

  const triggerProps = {
    ref: triggerRef,
    'aria-label': ariaLabel != null ? ariaLabel : (typeof content === 'string' ? content : undefined),
    'aria-describedby': visible ? tooltipId : undefined,
    // NO title attribute - prevents duplicate native tooltip
    onMouseEnter: handleShow,
    onMouseLeave: handleHide,
    onFocus: handleShow,
    onBlur: handleHide,
    style: baseStyle,
    ...rest,
  };

  let Trigger = asButton && !href ? 'button' : 'span';
  if (href) {
    Trigger = 'a';
    triggerProps.href = href;
    triggerProps.target = target ?? '_blank';
    triggerProps.rel = rel ?? 'noopener noreferrer';
  } else if (asButton) {
    triggerProps.type = 'button';
  } else {
    triggerProps.tabIndex = 0;
    triggerProps.role = 'button';
  }

  const tooltipStyle = {
    position: 'fixed',
    left: position ? `${position.x}px` : '-9999px',
    top: position ? `${position.y}px` : '-9999px',
    transform: position && position.positionAbove
      ? 'translateX(-50%) translateY(-100%)' // Above: translate up by tooltip height
      : 'translateX(-50%)', // Below: just center horizontally
    padding: 'var(--spacing-2) var(--spacing-3)',
    backgroundColor: 'var(--color-bg-elevated, #1f2937)',
    color: 'var(--color-text-inverse, #fff)',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-normal)',
    lineHeight: 1.4,
    borderRadius: 'var(--radius-md)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    whiteSpace: 'pre-line',
    maxWidth: '280px',
    zIndex: 10000,
    pointerEvents: 'none',
  };

  return (
    <>
      <Trigger {...triggerProps}>
        {children}
      </Trigger>
      {visible && position && typeof document !== 'undefined' && createPortal(
        <span
          id={tooltipId}
          role="tooltip"
          style={tooltipStyle}
        >
          {content}
        </span>,
        document.body
      )}
    </>
  );
}
